import { useQuery } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Notice, Plate } from "@wilco/ui";
import { formatMoney, t, type MessageKey } from "../i18n.ts";
import { errorNotice } from "../stock/ShelfPage.tsx";
import { costingApi, monthOf, validMonth } from "./api.ts";
import { CostingHead, MonthPicker } from "./CostingNav.tsx";

const route = getRouteApi("/app/costing/pnl");

/** Строки P&L по порядку отчёта (СВ-13); ключи ответа это PnlLine сервера. */
const LINES: [string, MessageKey][] = [
  ["REVENUE", "pnl.REVENUE"],
  ["COGS", "pnl.COGS"],
  ["RND", "pnl.RND"],
  ["REPAIRS", "pnl.REPAIRS"],
  ["SHORTAGES", "pnl.SHORTAGES"],
  ["PRODUCTION", "pnl.PRODUCTION"],
  ["ADJUSTMENTS", "pnl.ADJUSTMENTS"],
  ["OPEX", "pnl.OPEX"],
];

const ACTIVITIES = ["SER", "RND", "SRV", "STOCK"] as const;
const NONE = "";

/**
 * P&L месяца (СВ-13): строки отчёта, расходы по статьям и разрез
 * семейство × тип деятельности без пустых пересечений, доход плюс, затраты
 * минус. Неделимые затраты без типа стоят в своей колонке. Под правом
 * себестоимости (СВ-20).
 */
export function PnlPage() {
  const search = route.useSearch();
  const navigate = useNavigate();
  const month = validMonth(search.month, monthOf(-1));
  const pnl = useQuery({ queryKey: ["costing", "pnl", month], queryFn: () => costingApi.pnl(month) });
  const p = pnl.data;

  const families = p ? [...new Set(p.matrix.map((c) => c.family))].sort() : [];
  const columns = p ? [...ACTIVITIES.filter((a) => p.matrix.some((c) => c.activity === a)), ...(p.matrix.some((c) => !c.activity) ? [NONE] : [])] : [];
  const cell = (family: string, activity: string) => p?.matrix.find((c) => c.family === family && (c.activity ?? NONE) === activity)?.amount;
  const opex = p ? Object.entries(p.opex).filter(([, v]) => v !== 0) : [];

  return (
    <div className="page costing">
      <CostingHead title={t("costing.title")} />
      <div className="costing-bar">
        <MonthPicker month={month} onChange={(m) => void navigate({ to: "/costing/pnl", search: { month: m }, replace: true })} />
      </div>
      {pnl.error ? <Notice tone="danger" title={errorNotice(pnl.error).title} className="stock-notice" /> : null}
      {p && !p.opexConnected ? <Notice tone="info" title={t("pnl.opexOff")} className="stock-notice" /> : null}
      {p ? (
        <div className="costing-grid">
          <section className="stock-section" aria-labelledby="pnl-lines">
            <h2 id="pnl-lines" className="desk-group-title">
              {t("pnl.lines")}
            </h2>
            <dl className="cost-sum">
              {LINES.map(([key, label]) => (
                <div key={key}>
                  <dt>{t(label)}</dt>
                  <dd className="w-num">{formatMoney(p.lines[key] ?? 0, "UAH")}</dd>
                </div>
              ))}
              {opex.map(([article, amount]) => (
                <div key={article} data-sub="">
                  <dt>{article}</dt>
                  <dd className="w-num">{formatMoney(amount, "UAH")}</dd>
                </div>
              ))}
              <div data-total="">
                <dt>{t("pnl.result")}</dt>
                <dd className="w-num" data-negative={p.result < 0 ? "" : undefined}>
                  {formatMoney(p.result, "UAH")}
                </dd>
              </div>
            </dl>
          </section>

          <section className="stock-section" aria-labelledby="pnl-matrix">
            <h2 id="pnl-matrix" className="desk-group-title">
              {t("pnl.matrix")}
            </h2>
            {families.length === 0 ? (
              <p className="w-muted">{t("pnl.empty")}</p>
            ) : (
              <Plate cut={false} className="pnl-scroll">
                <table className="pnl-matrix">
                  <thead>
                    <tr>
                      <th scope="col">{t("pnl.family")}</th>
                      {columns.map((a) => (
                        <th key={a || "none"} scope="col" className="num">
                          {a || t("pnl.noActivity")}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {families.map((f) => (
                      <tr key={f}>
                        <th scope="row">{f}</th>
                        {columns.map((a) => {
                          const v = cell(f, a);
                          return (
                            <td key={a || "none"} className="num w-num" data-negative={v != null && v < 0 ? "" : undefined}>
                              {v == null ? "" : formatMoney(v, "UAH")}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Plate>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}
